/**
 * @module utils/string
 * @description String manipulation utilities for text transformations.
 * Provides functions for common string formatting operations including
 * capitalization, kebab-case, and camelCase conversions.
 * This module has no dependencies on other modules.
 */

/**
 * Capitalizes the first letter of a string
 * @function capitalize
 * @param {string} str - The string to capitalize
 * @returns {string} The string with the first letter in uppercase
 *
 * @example
 * capitalize('hello') // 'Hello'
 * capitalize('world') // 'World'
 */
export function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Converts a string to kebab-case format
 * @function kebabCase
 * @param {string} str - The string to convert (typically camelCase or PascalCase)
 * @returns {string} The string in kebab-case format (lowercase with hyphens)
 *
 * @example
 * kebabCase('camelCase') // 'camel-case'
 * kebabCase('PascalCase') // 'pascal-case'
 * kebabCase('someVariableName') // 'some-variable-name'
 */
export function kebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Converts a kebab-case string to camelCase format
 * @function camelCase
 * @param {string} str - The kebab-case string to convert
 * @returns {string} The string in camelCase format
 *
 * @example
 * camelCase('kebab-case') // 'kebabCase'
 * camelCase('some-variable-name') // 'someVariableName'
 */
export function camelCase(str: string): string {
    return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
}
